import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertTriangle, Flag } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const STATUS = { open: 'bg-amber-100 text-amber-800', closed: 'bg-slate-100 text-slate-700' };

const ACTIONS = [
    { value: 'dismiss', label: 'Descartar' },
    { value: 'warn', label: 'Advertir usuario' },
    { value: 'hide', label: 'Ocultar anuncio' },
    { value: 'suspend', label: 'Suspender usuario' },
    { value: 'delete', label: 'Eliminar anuncio' },
];

export default function AdminReports() {
    const [tab, setTab] = useState('open');
    const [items, setItems] = useState([]);
    const [actionMap, setActionMap] = useState({});
    const [notes, setNotes] = useState({});

    const load = async () => {
        const { data } = await api.get('/admin/reports', { params: { status: tab } });
        setItems(data || []);
    };
    useEffect(() => { load(); }, [tab]);

    const doAction = async (id) => {
        const action = actionMap[id];
        if (!action) { toast.error('Elige una acción'); return; }
        try {
            await api.post(`/admin/reports/${id}/action`, { action, notes: notes[id] || null });
            toast.success('Acción aplicada');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    return (
        <div className="space-y-4" data-testid="admin-reports-page">
            <div className="flex items-center gap-2"><Flag className="h-6 w-6 text-red-600" /><h1 className="text-2xl font-semibold">Reportes</h1></div>
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="open">Abiertos</TabsTrigger>
                    <TabsTrigger value="closed">Cerrados</TabsTrigger>
                </TabsList>
                <TabsContent value={tab} className="mt-4 space-y-3">
                    {items.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">Sin reportes</div>}
                    {items.map(r => (
                        <Card key={r.id} data-testid={`admin-report-${r.id}`}>
                            <CardContent className="p-4">
                                <div className="flex flex-wrap justify-between gap-3">
                                    <div>
                                        <div className="text-xs text-muted-foreground">Reportado por @{r.reporter?.nickname}</div>
                                        <div className="font-semibold">{r.target_type.toUpperCase()}: {r.target?.title || r.target?.nickname || r.target_id.slice(0,8)}</div>
                                        <div className="text-sm mt-1"><strong>Motivo:</strong> {r.reason}</div>
                                        <div className="text-sm text-muted-foreground mt-1">{r.description}</div>
                                        {r.evidence_url && <a href={r.evidence_url} target="_blank" rel="noreferrer" className="text-xs text-red-600 underline">Evidencia</a>}
                                    </div>
                                    <Badge className={STATUS[r.status] || ''}>{r.status}</Badge>
                                </div>
                                {r.status === 'open' && (
                                    <div className="mt-3 flex flex-wrap gap-2 items-end">
                                        <div className="min-w-[180px]">
                                            <Select value={actionMap[r.id]} onValueChange={v => setActionMap(m => ({...m, [r.id]: v}))}>
                                                <SelectTrigger data-testid={`report-action-${r.id}`}><SelectValue placeholder="Elige acción" /></SelectTrigger>
                                                <SelectContent>{ACTIONS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <Textarea rows={1} placeholder="Notas internas" value={notes[r.id] || ''} onChange={e => setNotes(n => ({...n, [r.id]: e.target.value}))} className="flex-1 min-w-[200px]" />
                                        <Button onClick={() => doAction(r.id)} className="bg-red-600 hover:bg-red-700" data-testid={`report-apply-${r.id}`}><AlertTriangle className="h-4 w-4 mr-1" />Aplicar</Button>
                                    </div>
                                )}
                                {r.action && <div className="mt-2 text-xs">Acción: <strong>{r.action}</strong> {r.reviewer_notes ? `— ${r.reviewer_notes}` : ''}</div>}
                            </CardContent>
                        </Card>
                    ))}
                </TabsContent>
            </Tabs>
        </div>
    );
}
